import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Info } from "lucide-react";
import { BeatLoader } from "react-spinners";
import { useModalContext } from "../../CustomModal/ModalProvider.jsx";
import { areaCamerasService } from "../../../services/areaCamerasService";
import { StyledButtonForm } from "../DetailsArea/styles";
import CameraPhotoViewer from "./PhotoViewer";

/** Mesmo bloco de Dados gerais / FIRMS: superfície clara, foto dentro do card. */
const Wrap = styled.section`
  width: 100%;
  min-width: 0;
  border: 1px solid var(--border-default);
  border-radius: 14px;
  background: var(--surface-1);
  padding: 1.15rem 1.25rem 1.35rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 768px) {
    padding: 1rem 0.9rem 1.15rem;
    border-radius: 12px;
  }
`;

const Heading = styled.h2`
  margin: 0;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--accent);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
`;

const Card = styled.figure`
  margin: 0;
  padding: 0.75rem;
  border: 1px solid var(--border-default);
  border-radius: 12px;
  background: var(--surface-2);
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  min-width: 0;
`;

const PhotoFrame = styled.button`
  appearance: none;
  width: 100%;
  height: 180px;
  padding: 0;
  border-radius: 10px;
  border: 1px solid var(--border-default);
  background: var(--surface-1);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: zoom-in;
`;

const Thumb = styled.img`
  display: block;
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
`;

const Caption = styled.figcaption`
  margin: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Empty = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: var(--text-secondary);
  line-height: 1.45;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  color: var(--text-secondary);
  font-size: 0.95rem;
`;

const UploadBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-default);
`;

const InfoBalloon = styled.span`
  position: absolute;
  bottom: calc(100% + 10px);
  left: 0;
  display: block;
  width: min(22rem, 72vw);
  padding: 0.7rem 0.85rem;
  border-radius: 10px;
  background: var(--surface-overlay);
  border: 1px solid var(--accent);
  box-shadow: var(--shadow-md);
  color: var(--accent);
  font-size: 0.78rem;
  font-weight: 500;
  line-height: 1.45;
  letter-spacing: normal;
  text-transform: none;
  text-align: left;
  white-space: normal;
  z-index: 40;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease;

  &::before {
    content: "";
    position: absolute;
    top: 100%;
    left: 7px;
    border: 6px solid transparent;
    border-top-color: var(--accent);
  }
`;

const InfoTitle = styled.span`
  display: block;
  margin: 0 0 0.4rem;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--accent);
`;

const InfoParagraph = styled.span`
  display: block;
  margin: 0;

  & + & {
    margin-top: 0.45rem;
  }
`;

const InfoHit = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  color: var(--accent);
  cursor: help;

  &::after {
    content: "";
    position: absolute;
    left: -6px;
    right: -6px;
    bottom: 100%;
    height: 12px;
  }

  &:hover ${InfoBalloon},
  &:focus-visible ${InfoBalloon} {
    opacity: 1;
    visibility: visible;
  }
`;

const CAMERA_DATE_DISCLAIMER = {
  title: "Disclaimer - Camera Trap Date and Time Records",
  paragraphs: [
    "Due to technical issues related to camera configuration, local settings, or battery performance, the dates and times displayed on camera trap images may not accurately reflect when the photographs were taken.",
    "We acknowledge and regret these inconsistencies. For the purposes of this report, please refer to the dates provided in the report itself rather than the timestamps embedded in the images.",
  ],
};

const HiddenInput = styled.input`
  display: none;
`;

function errorMessage(error, fallback) {
  const data = error?.response?.data;
  if (typeof data?.detail === "string") return data.detail;
  if (typeof data?.message === "string") return data.message;
  return fallback;
}

const AreaCamerasPanel = ({ areaId, archived = false }) => {
  const { showError } = useModalContext();
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;
  const photoInputRef = useRef(null);
  const archiveInputRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    if (!areaId) return undefined;
    let cancelled = false;
    setLoading(true);
    areaCamerasService
      .list(areaId)
      .then((list) => {
        if (!cancelled) setPhotos(list);
      })
      .catch((error) => {
        if (!cancelled) {
          showErrorRef.current(
            "Erro",
            errorMessage(error, "Não foi possível carregar as fotos desta área.")
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [areaId]);

  const refreshPage = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("aba", "cameras");
    window.location.assign(url.toString());
  };

  const onPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      await areaCamerasService.uploadPhoto(areaId, file);
      refreshPage();
    } catch (error) {
      setUploading(false);
      showError("Erro", errorMessage(error, "Não foi possível enviar a foto."));
    }
  };

  const onArchive = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const name = (file.name || "").toLowerCase();
    if (!name.endsWith(".zip") && !name.endsWith(".rar")) {
      showError("Arquivo inválido", "Envie um arquivo .zip ou .rar.");
      return;
    }
    setUploading(true);
    try {
      await areaCamerasService.uploadArchive(areaId, file);
      refreshPage();
    } catch (error) {
      setUploading(false);
      showError(
        "Erro",
        errorMessage(error, "Não foi possível enviar o arquivo.")
      );
    }
  };

  return (
    <Wrap>
      <Heading>Câmeras</Heading>

      {loading ? (
        <StatusRow role="status">
          <BeatLoader size={8} color="var(--accent)" />
          Carregando fotos…
        </StatusRow>
      ) : photos.length === 0 ? (
        <Empty>Nenhuma foto nesta área ainda.</Empty>
      ) : (
        <Grid>
          {photos.map((photo) => (
            <Card key={photo.id}>
              <PhotoFrame
                type="button"
                aria-label={`Abrir ${photo.original_name || "foto"} em tela cheia`}
                onClick={() => setViewer(photo)}
              >
                <Thumb
                  src={photo.image_url}
                  alt={photo.original_name || "Foto da área"}
                />
              </PhotoFrame>
              <Caption title={photo.original_name}>
                {photo.original_name || "Foto"}
              </Caption>
            </Card>
          ))}
        </Grid>
      )}

      <UploadBar>
        <StyledButtonForm
          type="button"
          disabled={archived || uploading || !areaId}
          onClick={() => photoInputRef.current?.click()}
        >
          {uploading ? "Enviando…" : "Enviar foto"}
        </StyledButtonForm>
        <StyledButtonForm
          type="button"
          secondary
          disabled={archived || uploading || !areaId}
          onClick={() => archiveInputRef.current?.click()}
        >
          Enviar ZIP ou RAR
        </StyledButtonForm>
        <InfoHit tabIndex={0} aria-label={CAMERA_DATE_DISCLAIMER.title}>
          <Info size={18} strokeWidth={2} aria-hidden="true" />
          <InfoBalloon role="tooltip">
            <InfoTitle>{CAMERA_DATE_DISCLAIMER.title}</InfoTitle>
            {CAMERA_DATE_DISCLAIMER.paragraphs.map((paragraph) => (
              <InfoParagraph key={paragraph}>{paragraph}</InfoParagraph>
            ))}
          </InfoBalloon>
        </InfoHit>
        <HiddenInput
          ref={photoInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/bmp,image/tiff,image/heic,.jpg,.jpeg,.png,.webp,.gif,.bmp,.tif,.tiff,.heic,.heif"
          onChange={onPhoto}
        />
        <HiddenInput
          ref={archiveInputRef}
          type="file"
          accept=".zip,.rar,application/zip,application/vnd.rar,application/x-rar-compressed"
          onChange={onArchive}
        />
      </UploadBar>
      <CameraPhotoViewer
        src={viewer?.image_url}
        alt={viewer?.original_name || "Foto da área"}
        onClose={() => setViewer(null)}
      />
    </Wrap>
  );
};

export default AreaCamerasPanel;
